import React, { useCallback, useEffect, useState, useRef } from 'react';
import { X } from 'lucide-react';
// --- Static Data ---
const NAMES = ['Juan', 'María', 'Carlos', 'Ana', 'Pedro', 'Laura', 'Diego', 'Sofía', 'Martín', 'Valentina', 'Fernando', 'Lucía', 'Roberto', 'Camila', 'Andrés', 'Paula', 'Gabriel', 'Carolina', 'Sebastián', 'Florencia', 'Alejandro', 'Daniela', 'Nicolás', 'Andrea', 'Matías', 'Claudia', 'Santiago', 'Patricia', 'Rodrigo', 'Marcela', 'José', 'Natalia', 'Luis', 'Verónica', 'Ricardo', 'Silvia', 'Gustavo', 'Marta', 'Eduardo', 'Graciela'];
const CITIES = ['Montevideo', 'Salto', 'Paysandú', 'Las Piedras', 'Rivera', 'Maldonado', 'Tacuarembó', 'Melo', 'Mercedes', 'Artigas', 'Minas', 'San José', 'Durazno', 'Florida', 'Treinta y Tres', 'Rocha', 'Colonia', 'Carmelo', 'Fray Bentos', 'Trinidad', 'Canelones', 'Punta del Este', 'Ciudad de la Costa'];
type Product = {
  title: string;
  price: string;
  image: string;
  weight?: number;
};
const FALLBACK_PRODUCTS: Product[] = [{
  title: 'El Protocolo de Limpieza Glicémica',
  price: '$290 UYU',
  image: 'https://cdn.magicpatterns.com/uploads/swco9uh88fgzGaxD6EYQFz/Mockup.png',
  weight: 0.7
}, {
  title: 'El Recetario de la Feria',
  price: '$397 UYU',
  image: 'https://cdn.magicpatterns.com/uploads/dr7pQu83eN59infjLTCt4j/Recetario_169.png',
  weight: 0.15
}, {
  title: 'La Polis del Páncreas',
  price: '$397 UYU',
  image: 'https://cdn.magicpatterns.com/uploads/4EBSQLZ9ZkWpyeBWxkuqPx/La_polis_169.png',
  weight: 0.15
}];
type ToastData = {
  name: string;
  city: string;
  product: Product;
  timeAgo: number;
};
export function RecentPurchaseToast() {
  const [toastData, setToastData] = useState<ToastData | null>(null);
  const [isShowing, setIsShowing] = useState(false);
  const [products, setProducts] = useState<Product[]>(FALLBACK_PRODUCTS);
  const recentNames = useRef<string[]>([]);
  const isDismissed = useRef(false);
  // --- DOM Scanner ---
  useEffect(() => {
    try {
      const scannedProducts: Product[] = [];
      // Attempt to scan common product cards/bumps on the page
      const elements = document.querySelectorAll('.bump-shimmer, .bg-white.rounded-xl');
      elements.forEach(el => {
        const text = el.textContent || '';
        const priceMatch = text.match(/\$\s?(\d+(?:\.\d{2})?)/);
        const img = el.querySelector('img');
        if (priceMatch && img && img.src) {
          const titleEl = el.querySelector('h3, h4, strong, p.font-bold');
          if (titleEl) {
            // Clean up emojis and extra text from title
            const cleanTitle = titleEl.textContent?.replace(/🍽️|📈|✅/g, '').split('—')[0].trim() || 'Producto';
            // Avoid duplicates
            if (!scannedProducts.some(p => p.title === cleanTitle)) {
              scannedProducts.push({
                title: cleanTitle,
                price: `$${priceMatch[1]} UYU`,
                image: img.src
              });
            }
          }
        }
      });
      if (scannedProducts.length > 0) {
        setProducts(scannedProducts);
      }
    } catch (e) {
      console.error('Error scanning products, using fallback', e);
    }
  }, []);
  // --- Random Selection Logic ---
  const pickRandomData = useCallback((): ToastData => {
    // 1. Pick Name (avoiding last 10)
    const availableNames = NAMES.filter(n => !recentNames.current.includes(n));
    const name = availableNames.length > 0 ? availableNames[Math.floor(Math.random() * availableNames.length)] : NAMES[Math.floor(Math.random() * NAMES.length)];
    recentNames.current = [name, ...recentNames.current].slice(0, 10);
    // 2. Pick City & Time
    const city = CITIES[Math.floor(Math.random() * CITIES.length)];
    const timeAgo = Math.floor(Math.random() * 5) + 1;
    // 3. Pick Product
    let product: Product;
    if (products === FALLBACK_PRODUCTS) {
      // Use weighted selection for fallback
      const r = Math.random();
      if (r <= 0.7) product = products[0];else if (r <= 0.85) product = products[1];else product = products[2];
    } else {
      // Uniform selection for scanned products
      product = products[Math.floor(Math.random() * products.length)];
    }
    return {
      name,
      city,
      product,
      timeAgo
    };
  }, [products]);
  // --- Timing Loop ---
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    let isMounted = true;
    const runLoop = () => {
      if (!isMounted) return;
      isDismissed.current = false;
      const data = pickRandomData();
      setToastData(data);
      // Small delay to allow DOM to render before animating in
      timeoutId = setTimeout(() => {
        if (!isMounted || isDismissed.current) return;
        setIsShowing(true);
        // Hide after 4 seconds
        timeoutId = setTimeout(() => {
          if (!isMounted || isDismissed.current) return;
          hideToast();
        }, 4000);
      }, 50);
    };
    const hideToast = () => {
      setIsShowing(false);
      // Wait for exit animation (500ms) then schedule next
      timeoutId = setTimeout(() => {
        if (!isMounted) return;
        setToastData(null);
        // Random interval between 8s and 15s
        const nextInterval = Math.floor(Math.random() * (15000 - 8000 + 1)) + 8000;
        timeoutId = setTimeout(runLoop, nextInterval);
      }, 500);
    };
    // Initial delay of 3 seconds
    timeoutId = setTimeout(runLoop, 3000);
    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [pickRandomData]);
  const handleDismiss = () => {
    isDismissed.current = true;
    setIsShowing(false);
  };
  if (!toastData) return null;
  return <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] w-[90%] max-w-sm pointer-events-none transition-all duration-500 ease-out motion-reduce:transition-none
        ${isShowing ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`} data-id="element-420">
      <div className="bg-white rounded-2xl shadow-2xl border border-emerald-100 p-3 flex items-center gap-4 pointer-events-auto relative overflow-hidden" data-id="element-421">
        {/* Subtle accent line */}
        <div className="absolute left-0 top-0 bottom-0 w-1 bg-emerald-500" data-id="element-422" />

        {/* Close Button */}
        <button onClick={handleDismiss} className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors p-1" aria-label="Cerrar notificación" data-id="element-423">
          <X className="h-3.5 w-3.5" data-id="element-424" />
        </button>

        {/* Product Image */}
        <div className="flex-shrink-0" data-id="element-425">
          <img src={toastData.product.image} alt={toastData.product.title} className="w-12 h-12 rounded-full object-cover border border-gray-100 shadow-sm" data-id="element-426" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0 pr-4" data-id="element-427">
          <p className="text-sm text-gray-600 leading-tight mb-0.5" data-id="element-428">
            <span className="font-bold text-gray-900" data-id="element-429">{toastData.name}</span> en{' '}
            {toastData.city} acaba de comprar
          </p>
          <p className="text-sm font-bold text-emerald-700 truncate" data-id="element-430">
            {toastData.product.title} — {toastData.product.price}
          </p>
          <p className="text-xs text-gray-400 mt-1 font-medium" data-id="element-431">
            Hace {toastData.timeAgo} minuto{toastData.timeAgo > 1 ? 's' : ''}
          </p>
        </div>
      </div>
    </div>;
}